/*----------------------------------
- AGENDA FEED COPY BUTTON
----------------------------------*/

/**
 * @docs docs/features/agenda
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import StrokeIcon from '@uniquedomains/components/ui/stroke-icon';

import { AGENDA_PAGE_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import useCopyToClipboard from '@/client/hooks/useCopyToClipboard';
import { track } from '@/client/pages/landing/lib/analytics';

/*----------------------------------
- TYPES
----------------------------------*/

type AgendaFeedCopyButtonProps = {
    /** The feed's site path (`/agenda/launches.ics`): copied as an absolute URL. */
    href: string;
    /** `all` or the feed's category: the analytics value and the test id suffix. */
    feedKey: string;
    label: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Copies one feed's link, the address a calendar app subscribes to, and says so for a moment.
 * Renders "Copy link" on the server and on hydration alike; the URL is built on click, from the
 * page's own origin, so the server never needs to know it.
 */
export default function AgendaFeedCopyButton({ href, feedKey, label }: AgendaFeedCopyButtonProps) {
    const content = AGENDA_PAGE_CONTENT.feeds;
    const { copied, copy } = useCopyToClipboard();

    return (
        <button
            type="button"
            onClick={() => {
                track('agenda_ics_click', { category: feedKey, action: 'copy' });
                void copy(new URL(href, window.location.origin).toString());
            }}
            aria-label={content.copyLabel(label)}
            class="inline-flex min-h-[30px] flex-none items-center gap-1.5 rounded-full border border-[rgba(27,29,30,0.12)] bg-white px-2.5 text-[12px] font-bold text-[#1b1d1e] transition-colors hover:border-[rgba(27,29,30,0.3)]"
            data-testid={`agenda-ics-copy-${feedKey}`}
        >
            <StrokeIcon name="copy" size={12} strokeWidth={2.2} />
            <span aria-live="polite">{copied ? content.copied : content.copy}</span>
        </button>
    );
}
